import { createSlice } from '@reduxjs/toolkit';
import { checkBusinessEntity } from '../../../thunks/megalanding/checkBusinessEntity';
import { errorCodes } from '../../../services/megalanding/checkBusinessEntity';
import { IPublicV1PublicOfferCheckBusinessEntityResponseSchema } from '../../../repositories/dailyrent/entities/response/PublicV1PublicOfferCheckBusinessEntityResponseSchema';
import { acceptBusinessEntityPublicOffer } from 'shared/thunks/businessEntityFlow/acceptBusinessEntityPublicOffer';

export type TBusinessEntityValidationState = {
  status: {
    isAwaiting: boolean;
    isPending: boolean;
    isError: boolean;
    isSuccess: boolean;
  };
  businessEntityFoundData: null | IPublicV1PublicOfferCheckBusinessEntityResponseSchema;
  publicOfferAccepted: boolean;
  errorCode: (typeof errorCodes)[number] | 'unknown' | null;
};

const initialState: TBusinessEntityValidationState = {
  status: {
    isAwaiting: true,
    isPending: false,
    isError: false,
    isSuccess: false,
  },
  businessEntityFoundData: null,
  publicOfferAccepted: false,
  errorCode: null,
};

const setStatusesToTrue = (
  state: TBusinessEntityValidationState,
  ...statuses: Array<keyof TBusinessEntityValidationState['status']>
) => {
  Object.keys(state.status).forEach((key: keyof TBusinessEntityValidationState['status']) => {
    state.status[key] = statuses.includes(key);
  });
};

const businessEntityValidationReducer = createSlice({
  name: 'businessEntityValidation',
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers(builder) {
    builder.addCase(checkBusinessEntity.pending, state => {
      setStatusesToTrue(state, 'isPending');
      state.errorCode = null;
    });
    builder.addCase(checkBusinessEntity.rejected, (state, action) => {
      setStatusesToTrue(state, 'isError', 'isAwaiting');
      state.errorCode = action.payload || 'unknown';
    });
    builder.addCase(checkBusinessEntity.fulfilled, (state, action) => {
      setStatusesToTrue(state, 'isSuccess', 'isAwaiting');
      state.businessEntityFoundData = action.payload;
    });

    builder.addCase(acceptBusinessEntityPublicOffer.pending, state => {
      setStatusesToTrue(state, 'isPending');
      state.errorCode = null;
    });
    builder.addCase(acceptBusinessEntityPublicOffer.rejected, state => {
      setStatusesToTrue(state, 'isError', 'isAwaiting');
      state.errorCode = 'unknown';
    });
    builder.addCase(acceptBusinessEntityPublicOffer.fulfilled, state => {
      setStatusesToTrue(state, 'isSuccess', 'isAwaiting');
      state.publicOfferAccepted = true;
    });
  },
});

export const {
  reducer: businessEntityValidation,
  actions: { resetState },
} = businessEntityValidationReducer;
